import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { Button } from "../components/common/Button";
import { Calendar, Clock, Users, MapPin, CheckCircle, Gift } from "lucide-react";
import { Spinner } from "../components/common/Spinner";
import { ImageCarousel } from "../components/common/ImageCarousel";
import { ReservationPaymentNotice } from "../components/common/ReservationPaymentNotice";
import { KitOptionPicker } from "../components/common/KitOptionPicker";
import styles from "./WorkshopsPage.module.css";

const WORKSHOP_GALLERY = [
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.14 PM.jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.14 PM (1).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.14 PM (2).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.14 PM (3).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.14 PM (4).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.14 PM (5).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM.jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (1).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (2).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (3).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (4).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (5).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (6).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (7).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (8).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (9).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.15 PM (10).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM.jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (1).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (2).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (3).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (4).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (5).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (6).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (7).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (8).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (9).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (10).jpeg",
  "/Workshops catalogue/WhatsApp Image 2026-07-19 at 12.08.16 PM (11).jpeg",
];

interface WorkshopKit {
  id: string;
  name: string;
  image: string;
}

interface Workshop {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  endTime: string;
  price: number;
  capacity: number;
  spotsLeft: number;
  image: string;
  instructor: string;
  level: string;
  location: string;
  address: string;
  availableKits: WorkshopKit[];
  bundleName: string;
  bundleDescription: string;
  bundleImage: string;
}

function getImageSrc(url: string) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE_URL}${url}`;
}

export function WorkshopsPage() {
  const navigate = useNavigate();

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(
    null,
  );
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    phone: "",
    numberOfSeats: 1,
    specialRequests: "",
    selectedKit: "",
    selectedFriendKit: "",
  });
  const [kitError, setKitError] = useState<string | null>(null);
  const [friendKitError, setFriendKitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/workshops/`);
        const data = await response.json();

        // Map backend data to frontend structure
        const mappedWorkshops: Workshop[] = data.workshops.map(
          (workshop: any) => ({
            id: workshop._id,
            title: workshop.name,
            description: workshop.description,
            date: workshop.date,
            time: workshop.time || "TBA",
            endTime: workshop.endTime || "",
            price: workshop.price,
            capacity: workshop.numberOfSeats,
            spotsLeft: workshop.availableSeats, // Real-time available seats from backend
            image: workshop.imageUrl
              ? getImageSrc(workshop.imageUrl)
              : "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop",
            instructor: "Sarah Ahmed", // Default instructor
            level: workshop.level || "beginner", // Default to beginner if not provided
            location: workshop.location,
            address: workshop.address || "",
            availableKits: (workshop.availableKits || []).map((kit: any) => ({
              id: kit._id,
              name: kit.name,
              image: kit.coverImage ? getImageSrc(kit.coverImage) : "",
            })),
            bundleName: workshop.bundleName || "",
            bundleDescription: workshop.bundleDescription || "",
            bundleImage: workshop.bundleImage || "",
          }),
        );

        setWorkshops(mappedWorkshops);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching workshops:", error);
        setLoading(false);
      }
    };

    fetchWorkshops();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "beginner":
        return styles.levelBeginner;
      case "intermediate":
        return styles.levelIntermediate;
      case "advanced":
        return styles.levelAdvanced;
      default:
        return "";
    }
  };

  const handleBookWorkshop = (workshop: Workshop) => {
    if (workshop.spotsLeft === 0) return;
    setSelectedWorkshop(workshop);
    setBookingForm((prev) => ({ ...prev, selectedKit: "", selectedFriendKit: "" }));
    setKitError(null);
    setFriendKitError(null);
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedWorkshop(null);
    setBookingForm({
      name: "",
      email: "",
      phone: "",
      numberOfSeats: 1,
      specialRequests: "",
      selectedKit: "",
      selectedFriendKit: "",
    });
    setBookingError(null);
    setBookingSuccess(false);
    setPhoneError(null);
    setKitError(null);
    setFriendKitError(null);
  };

  const validatePhoneNumber = (phone: string) => {
    if (!phone) {
      setPhoneError(null);
      return;
    }

    const digitsOnly = phone.replace(/\D/g, "");

    if (digitsOnly.length > 0 && !digitsOnly.startsWith("0")) {
      setPhoneError("Egyptian phone number must start with 0");
      return;
    }

    if (digitsOnly.length > 11) {
      setPhoneError("Phone number cannot exceed 11 digits");
      return;
    }

    if (digitsOnly.length > 0 && digitsOnly.length < 11) {
      setPhoneError(
        `${11 - digitsOnly.length} more digit${11 - digitsOnly.length === 1 ? "" : "s"} needed`,
      );
      return;
    }

    setPhoneError(null);
  };

  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    // Real-time phone validation
    if (name === "phone") {
      validatePhoneNumber(value);
    }

    if (name === "numberOfSeats" && parseInt(value) === 1) {
      setFriendKitError(null);
      setBookingForm((prev) => ({
        ...prev,
        numberOfSeats: 1,
        selectedFriendKit: "",
      }));
      return;
    }

    setBookingForm((prev) => ({
      ...prev,
      [name]: name === "numberOfSeats" ? parseInt(value) : value,
    }));
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWorkshop) return;

    // Validate Egyptian phone number (must start with 0 and be 11 digits)
    const digitsOnly = bookingForm.phone.replace(/\D/g, "");
    if (!digitsOnly.startsWith("0") || digitsOnly.length !== 11) {
      setPhoneError(
        !digitsOnly.startsWith("0")
          ? "Egyptian phone number must start with 0"
          : "Egyptian phone number must be exactly 11 digits",
      );
      return;
    }

    if (selectedWorkshop.availableKits.length > 0 && !bookingForm.selectedKit) {
      setKitError("Please choose a kit for your workshop");
      return;
    }

    if (
      selectedWorkshop.availableKits.length > 0 &&
      bookingForm.numberOfSeats > 1 &&
      !bookingForm.selectedFriendKit
    ) {
      setFriendKitError("Please choose a kit for your friend");
      return;
    }

    setSubmitting(true);
    setBookingError(null);
    setPhoneError(null);
    setKitError(null);
    setFriendKitError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/reservations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          workshop: selectedWorkshop.id,
          guestName: bookingForm.name,
          guestEmail: bookingForm.email,
          guestPhone: bookingForm.phone,
          numberOfSeats: bookingForm.numberOfSeats,
          selectedKit: bookingForm.selectedKit || undefined,
          selectedFriendKit: bookingForm.selectedFriendKit || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create reservation");
      }

      setBookingSuccess(true);

      // Refresh workshops to get updated available seats
      const workshopsResponse = await fetch(`${API_BASE_URL}/api/workshops/`);
      const workshopsData = await workshopsResponse.json();
      const mappedWorkshops: Workshop[] = workshopsData.workshops.map(
        (workshop: any) => ({
          id: workshop._id,
          title: workshop.name,
          description: workshop.description,
          date: workshop.date,
          time: workshop.time || "TBA",
          endTime: workshop.endTime || "",
          price: workshop.price,
          capacity: workshop.numberOfSeats,
          spotsLeft: workshop.availableSeats,
          image: workshop.imageUrl
            ? getImageSrc(workshop.imageUrl)
            : "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop",
          instructor: "Sarah Ahmed",
          level: workshop.level || "beginner",
          location: workshop.location,
          address: workshop.address || "",
          availableKits: (workshop.availableKits || []).map((kit: any) => ({
            id: kit._id,
            name: kit.name,
            image: kit.coverImage ? getImageSrc(kit.coverImage) : "",
          })),
          bundleName: workshop.bundleName || "",
          bundleDescription: workshop.bundleDescription || "",
          bundleImage: workshop.bundleImage || "",
        }),
      );
      setWorkshops(mappedWorkshops);
    } catch (error: any) {
      setBookingError(error.message || "Failed to book workshop");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.workshopsPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Paint By Numbers Workshops</h1>
          <p className={styles.heroSubtitle}>
            Join our therapeutic painting sessions and discover your inner
            artist
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.mainContent} id="top">
        <div className={styles.container}>
          {/* Workshops List */}
          {loading ? (
            <Spinner label="Loading workshops..." size="lg" />
          ) : (
            <div className={styles.workshopsList}>
              {workshops.map((workshop) => (
                <div key={workshop.id} className={styles.workshopCard}>
                  <div className={styles.workshopImage}>
                    <img src={workshop.image || "/Workshop_Image.jpeg"} alt={workshop.title} />
                  </div>
                  <div className={styles.workshopContent}>
                    <h3 className={styles.workshopTitle}>{workshop.title}</h3>
                    <p className={styles.workshopDescription}>
                      {workshop.description}
                    </p>
                    {workshop.bundleName && (
                      <div className={styles.bundleBadge}>
                        <Gift size={16} />
                        <span>{workshop.bundleName}</span>
                      </div>
                    )}
                    <div className={styles.workshopDetails}>
                      <div className={styles.detailItem}>
                        <Calendar size={18} />
                        <span>{formatDate(workshop.date)}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <Clock size={18} />
                        <span>
                          {formatTime(workshop.time)}
                          {workshop.endTime ? ` - ${formatTime(workshop.endTime)}` : ""}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <MapPin size={18} />
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workshop.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.locationLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {workshop.address || workshop.location}
                        </a>
                      </div>
                      <div className={styles.detailItem}>
                        <Users size={18} />
                        <span>
                          {workshop.spotsLeft > 0
                            ? `${workshop.spotsLeft} spot${workshop.spotsLeft === 1 ? "" : "s"} available`
                            : "Fully Booked"}
                        </span>
                      </div>
                    </div>

                    <div className={styles.workshopFooter}>
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBookWorkshop(workshop);
                        }}
                        disabled={workshop.spotsLeft === 0}
                      >
                        {workshop.spotsLeft === 0 ? "Fully Booked" : "Book Now"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && workshops.length === 0 && (
            <div className={styles.noResults}>
              <p>No workshops found for this level.</p>
            </div>
          )}
        </div>
      </section>

      {/* Workshop Gallery Carousel */}
      <ImageCarousel
        images={WORKSHOP_GALLERY}
        title="From Our Previous Workshops"
        subtitle="A glimpse into the fun, creativity, and connections made at our paint sessions"
      />

      {/* Workshop Description */}
      <section className={styles.descriptionSection}>
        <div className={styles.descriptionContainer}>
          <p>
            Some of my favorite memories have been created around a table filled
            with brushes, colors, and people who had never painted before.
          </p>
          <p>
            These workshops were never just about finishing a painting. They
            were about slowing down, taking a break from everyday life, meeting
            new people, sharing conversations, and discovering how relaxing
            creativity can be.
          </p>
          <p>
            Over the years, every workshop had its own story. Some were
            seasonal, celebrating moments like the New Year with special
            collections. Others brought everyone together to paint one large
            artwork, where every person added a small part that became something
            beautiful when all the pieces came together. It was a simple
            reminder that creativity is even more meaningful when it's shared.
          </p>
          <p>
            One of the experiences closest to my heart was painting with the
            incredible fighters at Baheya Foundation. Watching art become a
            quiet space for comfort, expression, and stress relief reminded me
            why I started creating these experiences in the first place.
          </p>
          <p>
            You don't need to be an artist to join. All you need is a little
            curiosity and a willingness to slow down for a few hours. The
            painting is only part of what you'll take home — the conversations,
            the memories, the peaceful moments, and the feeling of creating
            something with your own hands are what stay with you long after the
            workshop ends.
          </p>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className={styles.expectSection}>
        <div className={styles.container}>
          <h2 className={styles.expectTitle}>What to Expect</h2>
          <div className={styles.expectGrid}>
            <div className={styles.expectCard}>
              <CheckCircle className={styles.expectIcon} />
              <h3>All Materials Provided</h3>
              <p>
                Canvas, paints, brushes, and all supplies included in the price
              </p>
            </div>
            <div className={styles.expectCard}>
              <CheckCircle className={styles.expectIcon} />
              <h3>Expert Guidance</h3>
              <p>Step-by-step instruction from experienced instructors</p>
            </div>
            <div className={styles.expectCard}>
              <CheckCircle className={styles.expectIcon} />
              <h3>Relaxing Environment</h3>
              <p>
                Peaceful space atmosphere perfect for creativity and mindfulness
              </p>
            </div>
            <div className={styles.expectCard}>
              <CheckCircle className={styles.expectIcon} />
              <h3>Take Home Your Art</h3>
              <p>Leave with a completed painting you'll be proud to display</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {showBookingModal && selectedWorkshop && (
        <div className={styles.modalOverlay} onClick={closeBookingModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={closeBookingModal}>
              ×
            </button>

            <h2 className={styles.modalTitle}>Book Workshop</h2>
            <h3 className={styles.modalWorkshopTitle}>
              {selectedWorkshop.title}
            </h3>

            <div className={styles.modalDetails}>
              <p>
                <strong>Date:</strong> {formatDate(selectedWorkshop.date)}
              </p>
              <p>
                <strong>Time:</strong> {formatTime(selectedWorkshop.time)}
                {selectedWorkshop.endTime ? ` - ${formatTime(selectedWorkshop.endTime)}` : ""}
              </p>
              <p>
                <strong>Address:</strong>{" "}
                {selectedWorkshop.address ? (
                  selectedWorkshop.address
                ) : (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedWorkshop.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.locationLink}
                  >
                    {selectedWorkshop.location}
                  </a>
                )}
              </p>
              <p>
                <strong>Price:</strong> EGP {selectedWorkshop.price}
              </p>
            </div>

            {selectedWorkshop.bundleName && (
              <div className={styles.bundleSection}>
                {selectedWorkshop.bundleImage && (
                  <img
                    src={getImageSrc(selectedWorkshop.bundleImage)}
                    alt={selectedWorkshop.bundleName}
                    className={styles.bundleImage}
                  />
                )}
                <div>
                  <h4 className={styles.bundleSectionTitle}>
                    <Gift size={18} />
                    {selectedWorkshop.bundleName}
                  </h4>
                  {selectedWorkshop.bundleDescription && (
                    <p className={styles.bundleSectionDesc}>
                      {selectedWorkshop.bundleDescription}
                    </p>
                  )}
                </div>
              </div>
            )}

            {bookingSuccess ? (
              <div className={styles.successMessage}>
                <CheckCircle size={48} style={{ color: "#10b981" }} />
                <h3>Reservation Submitted!</h3>
                <p>
                  Your reservation request has been received and is pending
                  approval. You will receive a confirmation email once the
                  business owner reviews and confirms your booking.
                </p>
                <ReservationPaymentNotice />
              </div>
            ) : (
              <form
                className={styles.bookingForm}
                onSubmit={handleBookingSubmit}
              >
                {bookingError && (
                  <div className={styles.errorMessage}>{bookingError}</div>
                )}
                <div className={styles.formGroup}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={bookingForm.name}
                    onChange={handleFormChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={bookingForm.email}
                    onChange={handleFormChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={bookingForm.phone}
                    onChange={handleFormChange}
                    placeholder="01XXXXXXXXX (11 digits)"
                    required
                    style={{
                      borderColor: phoneError ? "#ef4444" : undefined,
                      borderWidth: phoneError ? "2px" : undefined,
                    }}
                  />
                  {phoneError && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.875rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      ⚠️ {phoneError}
                    </span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label>Number of Spots</label>
                  <select
                    name="numberOfSeats"
                    value={bookingForm.numberOfSeats}
                    onChange={handleFormChange}
                    required
                  >
                    {[...Array(Math.min(selectedWorkshop.spotsLeft, 5))].map(
                      (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                {selectedWorkshop.availableKits.length > 0 && (
                  <div className={styles.formGroup}>
                    <label>Choose Your Kit</label>
                    <KitOptionPicker
                      kits={selectedWorkshop.availableKits}
                      value={bookingForm.selectedKit}
                      onChange={(kitId) => {
                        setKitError(null);
                        setBookingForm((prev) => ({
                          ...prev,
                          selectedKit: kitId,
                        }));
                      }}
                      hasError={!!kitError}
                    />
                    {kitError && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontSize: "0.875rem",
                          marginTop: "0.25rem",
                          display: "block",
                        }}
                      >
                        ⚠️ {kitError}
                      </span>
                    )}
                  </div>
                )}
                {selectedWorkshop.availableKits.length > 0 &&
                  bookingForm.numberOfSeats > 1 && (
                    <div className={styles.formGroup}>
                      <label>Your Friend's Kit</label>
                      <KitOptionPicker
                        kits={selectedWorkshop.availableKits}
                        value={bookingForm.selectedFriendKit}
                        onChange={(kitId) => {
                          setFriendKitError(null);
                          setBookingForm((prev) => ({
                            ...prev,
                            selectedFriendKit: kitId,
                          }));
                        }}
                        hasError={!!friendKitError}
                      />
                      {friendKitError && (
                        <span
                          style={{
                            color: "#ef4444",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                            display: "block",
                          }}
                        >
                          ⚠️ {friendKitError}
                        </span>
                      )}
                      {bookingForm.numberOfSeats > 3 && (
                        <p className={styles.kitGroupNote}>
                          For groups larger than 3, we'll confirm the
                          remaining kit choices with you via WhatsApp when we
                          contact you.
                        </p>
                      )}
                    </div>
                  )}
                <div className={styles.formGroup}>
                  <label>Special Requests (Optional)</label>
                  <textarea
                    name="specialRequests"
                    value={bookingForm.specialRequests}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Any special requirements or questions?"
                  ></textarea>
                </div>
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? "Booking..." : "Confirm Booking"}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
